<script setup lang="ts">
import { generateImgUrl, slugify } from '~/utils/format'
import { SESSION_CHECKOUT_PAGE_VISITED } from '~/stores/checkout'

const DISMISS_KEY = 'byhours_checkout_recovery_dismissed'

const { locale, t } = useI18n()
const checkoutStore = useCheckoutStore()
const currencyStore = useCurrencyStore()
const { currency } = storeToRefs(currencyStore)
const deviceStore = useDeviceStore()
const { trackCheckoutRecoveryHotelLinkClicked, trackCheckoutRecoveryFinishClicked } = usePosthogFunnel()

const visible = ref(false)

function hasRecoverableCheckout(): boolean {
  checkoutStore.getCheckoutData()
  if (!checkoutStore.hasValidData()) return false
  // Solo carrito abandonado: hubo visita al checkout y salida sin comprar
  return sessionStorage.getItem(SESSION_CHECKOUT_PAGE_VISITED) === '1'
}

function syncVisibility() {
  if (!import.meta.client) return
  const dismissed = sessionStorage.getItem(DISMISS_KEY) === '1'
  visible.value = hasRecoverableCheckout() && !dismissed
}

const hotelName = computed(() => {
  const data = checkoutStore.getCheckoutData()
  return String(data?.hotelData?.name ?? '').trim() || t('checkout.summary.hotel')
})

const hotelImageSrc = computed(() => {
  const data = checkoutStore.getCheckoutData()
  const raw = data?.hotelData?.images?.[0]
  return raw ? generateImgUrl(String(raw)) : ''
})

/** Misma convención que SearchCard / BookingCard: `/{locale}/{slug}-{id}.html` */
const hotelDetailPath = computed(() => {
  const data = checkoutStore.getCheckoutData()
  const hotel = data?.hotelData as { id?: number; name?: string; slug?: string } | undefined
  if (!hotel?.id) return null
  const slug = String(hotel.slug || slugify(hotel.name || 'hotel'))
  return `/${locale.value}/${slug}-${hotel.id}.html`
})

const oneStepCheckoutPath = computed(() => `/${locale.value}/v2/one-step-check-out/`)

onMounted(() => {
  syncVisibility()
})

watch(() => checkoutStore.checkoutData, syncVisibility)

function dismiss() {
  if (import.meta.client) {
    sessionStorage.setItem(DISMISS_KEY, '1')
  }
  visible.value = false
}

function recoveryPosthogPayload() {
  const data = checkoutStore.getCheckoutData()
  const hotel = data?.hotelData as { id?: number; name?: string } | undefined
  const room = data?.roomData as { id?: number } | undefined
  return {
    hotel_id: hotel?.id ?? 0,
    hotel_name: hotel?.name,
    room_id: room?.id,
    total_amount: data?.totalAmount,
    currency: currency.value,
    locale: locale.value,
    is_mobile: deviceStore.isMobile
  }
}

function onHotelLinkClick() {
  const p = recoveryPosthogPayload()
  trackCheckoutRecoveryHotelLinkClicked({
    hotel_id: p.hotel_id || 0,
    hotel_name: p.hotel_name,
    locale: p.locale,
    is_mobile: p.is_mobile
  })
  dismiss()
}

async function onFinishBookingClick() {
  const p = recoveryPosthogPayload()
  trackCheckoutRecoveryFinishClicked({
    hotel_id: p.hotel_id || 0,
    hotel_name: p.hotel_name,
    room_id: p.room_id,
    total_amount: p.total_amount,
    currency: p.currency,
    locale: p.locale,
    is_mobile: p.is_mobile
  })
  await navigateTo(oneStepCheckoutPath.value)
}
</script>

<template>
  <div
    v-show="visible"
    class="fixed bottom-3 left-1/2 z-10000000000 w-[min(100%-0.75rem,22rem)] -translate-x-1/2 px-1.5 pb-[max(0.25rem,env(safe-area-inset-bottom))] sm:bottom-4 sm:w-[min(100%-1.5rem,36rem)] sm:px-0"
    role="dialog"
    :aria-hidden="!visible"
    :aria-label="t('checkout.recovery.title')"
  >
    <div class="checkout-recovery-enter w-full">
      <div
        class="flex flex-row items-stretch overflow-hidden rounded-2xl border border-neutral-200/80 bg-white shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)] sm:rounded-3xl"
      >
          <div
            class="relative min-h-19 w-[30%] max-w-26 shrink-0 self-stretch sm:min-h-32 sm:max-w-none sm:w-[46%]"
          >
            <NuxtImg
              v-if="hotelImageSrc"
              provider="byhours"
              format="webp"
              width="400"
              height="280"
              :src="hotelImageSrc"
              alt=""
              class="absolute inset-0 size-full min-h-19 object-cover sm:min-h-32"
              loading="lazy"
            />
            <div
              v-else
              class="absolute inset-0 flex min-h-19 items-center justify-center bg-byhours-50 sm:min-h-32"
            >
              <UIcon name="i-lucide-building-2" class="size-7 text-primary/40 sm:size-10" />
            </div>
          </div>

          <div
            class="relative flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-1 py-3 pl-2 pr-7 sm:min-h-0 sm:gap-1 sm:px-5 sm:py-6 sm:pr-6"
          >
            <UButton
              color="neutral"
              variant="ghost"
              size="xs"
              class="absolute right-1 top-0.5 rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 sm:right-2 sm:top-2"
              :aria-label="t('checkout.recovery.closeAria')"
              icon="i-lucide-x"
              @click="dismiss"
            />

            <p class="pr-1 text-xs font-medium leading-tight text-neutral-500 sm:pr-8 sm:text-xs">
              {{ t('checkout.recovery.title') }}
            </p>

            <p
              class="line-clamp-2 pr-1 text-sm font-bold leading-tight text-neutral-900 sm:line-clamp-none sm:pr-6 sm:text-lg sm:leading-snug"
            >
              <NuxtLink
                v-if="hotelDetailPath"
                :to="hotelDetailPath"
                class="underline decoration-neutral-900 underline-offset-2 sm:underline-offset-4"
                @click="onHotelLinkClick"
              >
                {{ hotelName }}
              </NuxtLink>
              <span v-else class="underline decoration-neutral-900 underline-offset-2 sm:underline-offset-4">{{ hotelName }}</span>
            </p>

            <div class="mt-1 sm:mt-4">
              <UButton
                :label="t('checkout.recovery.finishBooking')"
                color="neutral"
                size="xs"
                class="min-w-0 justify-center bg-byhours-gradient shadow-sm"
                :ui="{
                  base: 'px-4 py-2 text-xs font-semibold text-white sm:px-6 sm:py-2.5 sm:text-sm'
                }"
                @click="onFinishBookingClick"
              />
            </div>
          </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Solo translateY/opacity: el padre lleva -translate-x-1/2 y no debe pisarse el transform */
.checkout-recovery-enter {
  animation: checkout-recovery-in 0.3s ease-out;
}

@keyframes checkout-recovery-in {
  from {
    opacity: 0;
    transform: translateY(1.5rem);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
